import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation } from '@tanstack/react-query';
import {
  Vehicle,
  VehicleType,
  VehicleCreateInput,
} from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { Modal, ModalBody, ModalFooter } from '../common/Modal';
import { FormField } from '../common/FormField';
import {
  btnPrimary,
  btnSecondary,
  fieldGridClass,
  inputClass,
  selectClass,
} from '../common/formStyles';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const VehicleForm = ({
  vehicle,
  onClose,
  onSuccess,
}: VehicleFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<VehicleCreateInput>({
    name: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '',
    licensePlate: '',
    type: 'car',
    currentMileage: 0,
    photoUrl: '',
    specifications: {},
  });

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        vin: vehicle.vin || '',
        licensePlate: vehicle.licensePlate || '',
        type: vehicle.type,
        currentMileage: vehicle.currentMileage,
        photoUrl: vehicle.photoUrl || '',
        specifications: vehicle.specifications || {},
      });
      if (vehicle.photoUrl) {
        setPhotoPreview(vehicle.photoUrl);
      }
    }
  }, [vehicle]);

  const createMutation = useMutation({
    mutationFn: async (data: VehicleCreateInput) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.post(`${API_URL}/vehicles`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('vehicle.addSuccess'));
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<VehicleCreateInput>) => {
      if (!user || !vehicle) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.put(
        `${API_URL}/vehicles/${vehicle.id}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('vehicle.updateSuccess'));
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const response = await axios.post(`${API_URL}/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data.url;
    },
    onSuccess: (url) => {
      setFormData((prev) => ({ ...prev, photoUrl: url }));
      setPhotoPreview(url);
      toast.success(t('vehicle.photoUploaded'));
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('vehicle.invalidImageType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('vehicle.fileTooLarge'));
      return;
    }

    setUploadingPhoto(true);
    try {
      await uploadPhotoMutation.mutateAsync(file);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({ ...prev, photoUrl: '' }));
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...formData,
      vin: formData.vin || undefined,
      licensePlate: formData.licensePlate || undefined,
      photoUrl: formData.photoUrl || undefined,
    };

    if (vehicle) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const vehicleTypes: VehicleType[] = ['car', 'motorcycle', 'bicycle', 'other'];
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1899 },
    (_, i) => currentYear - i
  );

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={vehicle ? t('vehicle.edit') : t('vehicle.add')}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <div className={fieldGridClass}>
            <FormField label={t('vehicle.name')} required>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('vehicle.type')} required>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type: e.target.value as VehicleType,
                  })
                }
                className={selectClass}
              >
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`vehicle.types.${type}`)}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('vehicle.make')} required>
              <input
                type="text"
                required
                value={formData.make}
                onChange={(e) =>
                  setFormData({ ...formData, make: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('vehicle.model')} required>
              <input
                type="text"
                required
                value={formData.model}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('vehicle.year')} required>
              <select
                required
                value={formData.year}
                onChange={(e) =>
                  setFormData({ ...formData, year: parseInt(e.target.value) })
                }
                className={selectClass}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('vehicle.currentMileage')} required>
              <input
                type="number"
                required
                min="0"
                step="1"
                value={formData.currentMileage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currentMileage: parseFloat(e.target.value) || 0,
                  })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('vehicle.vin')}>
              <input
                type="text"
                maxLength={17}
                value={formData.vin}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vin: e.target.value.toUpperCase(),
                  })
                }
                className={inputClass}
              />
            </FormField>

            <FormField label={t('vehicle.licensePlate')}>
              <input
                type="text"
                value={formData.licensePlate}
                onChange={(e) =>
                  setFormData({ ...formData, licensePlate: e.target.value })
                }
                className={inputClass}
              />
            </FormField>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('vehicle.photo')}
              </label>

              {photoPreview ? (
                <div className="relative">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="mb-2 h-36 w-full rounded-lg object-cover"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploadingPhoto
                        ? t('common.loading')
                        : t('vehicle.changePhoto')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                    >
                      {t('vehicle.removePhoto')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-4 text-center transition-colors hover:border-primary-500"
                >
                  <ImageIcon className="mx-auto mb-1 h-8 w-8 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    {t('vehicle.clickToUpload')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('vehicle.photoFormat')}
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={onClose} className={btnSecondary}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={isLoading} className={btnPrimary}>
            {isLoading ? t('common.loading') : t('common.save')}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
